import { CheckIcon } from "@phosphor-icons/react"
import React, { useMemo } from "react"

type ProgressStepsProps = {
    steps: ProgressStep[]
    type?: "primary" | "tertiary"
    className?: string
}

export type ProgressStep = {
    enabled: boolean
    completed: boolean
    current: boolean
    title?: string
    description?: string
    onClick?: () => void
}

type ProgressStepProps = {
    completed: boolean
    active: boolean
    enabled: boolean
    onClick?: () => void
    colors: {
        background: string
        border: string
        hover: string
        active: string
        onActive: string
    }
    title?: string
    description?: string
}

const COLOR_MAP = {
    primary: {
        background: "bg-primary",
        border: "border-primary",
        hover: "hover:bg-status-info-soft",
        active: "bg-status-info-soft",
        onActive: "text-primary",
    },
    tertiary: {
        background: "bg-tertiary",
        border: "border-tertiary",
        hover: "hover:bg-status-error-soft",
        active: "bg-status-error-soft",
        onActive: "text-tertiary",
    },
}

const ProgressStepComponent = ({
    completed,
    active,
    onClick,
    colors,
    enabled,
    title,
    description,
}: ProgressStepProps) => (
    <div className="relative flex min-w-12 flex-col items-center sm:min-w-16">
        <div
            className={`flex items-center justify-center ${
                onClick && enabled ? "cursor-pointer" : ""
            }`}
            onClick={onClick && enabled ? onClick : undefined}
        >
            {completed ? (
                <div
                    className={`peer ${active ? colors.active : colors.background} ${colors.border} flex h-7 w-7 items-center justify-center rounded-full border-2 border-solid sm:h-8 sm:w-8 ${onClick && enabled ? colors.hover : ""} `}
                >
                    <CheckIcon
                        size={16}
                        weight="bold"
                        className={`sm:h-4 sm:w-4 ${
                            active ? colors.onActive : "text-text-on-primary"
                        }`}
                    />
                </div>
            ) : (
                <div
                    className={`${
                        active ? colors.active : "bg-background-page"
                    } ${colors.border} flex h-7 w-7 items-center justify-center rounded-full border-2 border-solid sm:h-8 sm:w-8 ${onClick && enabled ? colors.hover : ""} `}
                />
            )}
        </div>
        {(title || description) && (
            <div className="absolute top-10 left-1/2 flex -translate-x-1/2 -translate-y-0 flex-col items-center gap-1 sm:top-12">
                {title && (
                    <p className="text-text-primary text-xs font-bold text-nowrap sm:text-sm">
                        {title}
                    </p>
                )}
                {description && (
                    <p className="text-text-secondary text-xs text-nowrap sm:text-sm">
                        {description}
                    </p>
                )}
            </div>
        )}
    </div>
)

const ProgressSteps = ({
    steps,
    type = "primary",
    className,
}: ProgressStepsProps) => {
    const colors = useMemo(() => COLOR_MAP[type], [type])
    return (
        <div className="flex w-full flex-col items-center">
            <div
                className={`flex min-h-24 w-full items-center justify-between px-2 pb-8 sm:min-h-32 sm:px-4 sm:pb-12 ${className}`}
            >
                {steps.map((step, index) => (
                    <React.Fragment key={index}>
                        <ProgressStepComponent
                            completed={step.completed}
                            active={step.current}
                            onClick={step.onClick}
                            enabled={step.enabled}
                            colors={colors}
                            title={step.title}
                            description={step.description}
                        />
                        {index < steps.length - 1 && (
                            <div
                                className={`${colors.background} mx-1 h-1 flex-1 sm:mx-2`}
                            />
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    )
}

export { ProgressSteps }
